import React from "react";
import Layout from "./components/layout";
import { track } from "./analytics";

const services = [
  [
    "software-architecture",
    "Custom software & systems architecture",
    "From architecture to production implementation.",
    "Turn a difficult product brief into a clear architecture, working backend and maintainable system. We design APIs, integrate existing platforms and take prototypes into production.",
    "Architecture reviews · Backend platforms · APIs & integrations",
  ],
  [
    "embedded-iot",
    "Embedded, IoT & edge systems",
    "Make connected devices work in the field.",
    "Engineer the full path from device software to cloud services, with provisioning, OTA updates, fleet diagnostics and secure remote access built for day-to-day operation.",
    "Embedded Linux · ARM · Bluetooth / BLE · Device-to-cloud",
  ],
  [
    "ai-ml",
    "AI, ML & LLM engineering",
    "Move beyond a convincing demo.",
    "Build AI features with evaluation, failure analysis and observability. Combine LLMs, RAG, agents and computer vision with deterministic software that keeps the system under control.",
    "Applied ML · Evaluation · AI-backed APIs · Edge inference",
  ],
  [
    "mobile",
    "Mobile & product engineering",
    "Build the product around the whole system.",
    "Deliver Android applications that connect reliably to APIs, devices and users. Account for offline behavior, on-device AI, real-time visualization and the full publishing lifecycle.",
    "Kotlin / Android · Firebase · Subscriptions · API integration",
  ],
  [
    "high-reliability",
    "High-reliability & legacy systems",
    "Evolve the system you depend on.",
    "Diagnose difficult failures, modernize legacy software and strengthen testing, builds and deployment. Engineering experience shaped by aerospace and industrial constraints.",
    "C/C++ · Python · Ada · Real-time systems · Root-cause analysis",
  ],
];
const fit = [
  "Your prototype needs to become a production system.",
  "Your software needs to work with hardware or edge devices.",
  "Your AI feature works in demos but fails in real use.",
  "A legacy system is blocking your next product decision.",
  "Failures span several layers, and the root cause is unclear.",
  "You need someone to define the architecture and build it.",
];
const principles = [
  [
    "Architecture + implementation",
    "The same engineering perspective carries through design decisions, working code and debugging.",
  ],
  [
    "Cross-layer understanding",
    "We reason across devices, Linux, networking, APIs, data and product software to solve the whole problem.",
  ],
  [
    "Production discipline",
    "Diagnostics, validation, maintainability and controlled deployment are part of the engineering work.",
  ],
  [
    "Experience with difficult systems",
    "A background spanning aerospace, industrial instrumentation, AI, IoT and mobile products informs practical decisions.",
  ],
];
const work = [
  {
    name: "Resus",
    category: "SIMULATION / ANDROID",
    problem:
      "Make patient-monitor simulation available for training and study.",
    engineering:
      "Kotlin product architecture and real-time style monitor visualization, with an associated React web presence.",
    result:
      "End-to-end product engineering across a specialist Android application and the web.",
    href: "/resus",
    link: "Explore Resus",
  },
  {
    name: "Sonia AI",
    category: "ON-DEVICE AI / ANDROID",
    problem:
      "Bring AI music generation onto the device with a focus on privacy.",
    engineering: "Local AI model integration within an Android application.",
    result:
      "Applied on-device inference, connecting model execution to a usable mobile product.",
    href: "/sonia",
    link: "Explore Sonia AI",
  },
  {
    name: "Android product portfolio",
    category: "PRODUCT ARCHITECTURE / DELIVERY",
    problem: "Build and maintain products beyond their first release.",
    engineering:
      "Reusable architecture, API integration, Firebase, analytics and monetization across production applications.",
    result:
      "14+ published Android applications, demonstrating delivery and product lifecycle experience.",
    href: "/quantingoapps",
    link: "View published applications",
  },
];
function Arrow() {
  return <span aria-hidden="true">↗</span>;
}
export default function App() {
  return (
    <Layout>
      <section className="hero container" aria-labelledby="hero-title">
        <div className="hero-copy">
          <p className="eyebrow">Software engineering & systems architecture</p>
          <h1 id="hero-title">
            Engineering software for <span>complex, real-world systems.</span>
          </h1>
          <p className="hero-description">
            Quantingo designs and builds production software across
            embedded/IoT, AI, backend and mobile systems — combining senior
            systems architecture with hands-on engineering.
          </p>
          <div className="actions">
            <a
              className="button"
              href="/contact"
              onClick={() => track("hero_contact_click")}
            >
              Discuss your project <Arrow />
            </a>
            <a className="text-link" href="/services">
              Explore our capabilities <span aria-hidden="true">↓</span>
            </a>
          </div>
        </div>
        <div
          className="system-map"
          role="img"
          aria-label="Engineering across connected layers: product, intelligence, platforms, and devices, supported by architecture, integration and reliability."
        >
          <div className="map-heading">
            <span>THE WHOLE SYSTEM</span>
            <span aria-hidden="true">[ Q ]</span>
          </div>
          {[
            ["Product", "Mobile · Interfaces · Operations"],
            ["Intelligence", "AI · Models · Data"],
            ["Platforms", "Backends · APIs · Networking"],
            ["Devices", "Embedded Linux · Hardware · Edge"],
          ].map(([title, detail], index) => (
            <div className="system-layer" key={title}>
              <span className="layer-index">0{4 - index}</span>
              <div>
                <strong>{title}</strong>
                <span>{detail}</span>
              </div>
              <i />
            </div>
          ))}
          <div className="map-foundation">
            Architecture / Integration / Reliability
          </div>
        </div>
      </section>
      <div
        className="credibility container"
        aria-label="Engineering credentials"
      >
        <div>
          <strong>20+</strong>
          <span>years of engineering experience</span>
        </div>
        <div>
          <strong>28+</strong>
          <span>patent contributions</span>
        </div>
        <div>
          <strong>14+</strong>
          <span>published Android applications</span>
        </div>
        <p>
          Experience across aerospace, energy, industrial systems and edge
          computing.
        </p>
      </div>
      <section
        className="section container fit-section"
        aria-labelledby="fit-title"
      >
        <div>
          <p className="eyebrow">Where we can help</p>
          <h2 id="fit-title">When the challenge crosses boundaries.</h2>
          <p>
            You need a system that works as a whole. Quantingo is a strong fit
            when:
          </p>
        </div>
        <ul className="fit-list">
          {fit.map((item) => (
            <li key={item}>
              <span aria-hidden="true">↗</span>
              {item}
            </li>
          ))}
        </ul>
      </section>
      <section
        id="services"
        className="section services-section"
        aria-labelledby="services-title"
      >
        <div className="container">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Engineering services</p>
              <h2 id="services-title">
                From the first architecture
                <br />
                to the system in production.
              </h2>
            </div>
            <p>
              Senior technical direction and hands-on delivery, wherever the
              hard part lives.
            </p>
          </div>
          <div className="service-list">
            {services.map(
              ([id, title, promise, description, technologies], index) => (
                <article id={id} className="service-row" key={id}>
                  <span className="service-number">0{index + 1}</span>
                  <h3>{title}</h3>
                  <div>
                    <h4>{promise}</h4>
                    <p>{description}</p>
                    <p className="technologies">{technologies}</p>
                  </div>
                  <a
                    className="service-link"
                    href="/contact"
                    aria-label={`Discuss ${title.toLowerCase()}`}
                    onClick={() =>
                      track("service_contact_click", { service: id })
                    }
                  >
                    <Arrow />
                  </a>
                </article>
              ),
            )}
          </div>
        </div>
      </section>
      <section
        id="expertise"
        className="section expertise-section"
        aria-labelledby="expertise-title"
      >
        <div className="container">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Why Quantingo</p>
              <h2 id="expertise-title">
                See the architecture.
                <br />
                Understand the details.
              </h2>
            </div>
            <p>
              Technical decisions have consequences across the system. We work
              with those connections in view.{" "}
              <a href="/engineering">Our engineering approach</a>
            </p>
          </div>
          <div className="principles">
            {principles.map(([title, description], index) => (
              <article key={title}>
                <span className="principle-number">0{index + 1} /</span>
                <h3>{title}</h3>
                <p>{description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
      <section
        id="work"
        className="section container"
        aria-labelledby="work-title"
      >
        <div className="section-heading">
          <div>
            <p className="eyebrow">Selected work</p>
            <h2 id="work-title">Engineering made tangible.</h2>
          </div>
          <p>
            Quantingo’s own products demonstrate the connection between
            architecture and delivery.
          </p>
        </div>
        <div className="work-grid">
          {work.map((item) => (
            <article className="work-card" key={item.name}>
              <p className="eyebrow">{item.category}</p>
              <h3>{item.name}</h3>
              <dl>
                <dt>Problem</dt>
                <dd>{item.problem}</dd>
                <dt>Engineering</dt>
                <dd>{item.engineering}</dd>
                <dt>Capability demonstrated</dt>
                <dd>{item.result}</dd>
              </dl>
              <a
                href={item.href}
                onClick={() =>
                  track("selected_work_view", { project: item.name })
                }
              >
                {item.link} <Arrow />
              </a>
            </article>
          ))}
        </div>
      </section>
      <section
        id="about"
        className="section about-section"
        aria-labelledby="about-title"
      >
        <div className="container about-grid">
          <div>
            <p className="eyebrow">Experience behind Quantingo</p>
            <h2 id="about-title">
              Small by design.
              <br />
              Senior by default.
            </h2>
          </div>
          <div>
            <p className="large-copy">
              Quantingo is a founder-led software engineering and systems
              architecture consultancy for technically demanding products and
              systems.
            </p>
            <p>
              More than two decades of engineering experience connect
              mission-critical aerospace software, industrial sensing and AI/ML
              with embedded, IoT and mobile production systems.
            </p>
            <p>
              Founder experience includes engineering roles at{" "}
              <strong>Embraer</strong> and <strong>Halliburton</strong>. That
              background informs how we approach reliability, integration and
              real-world constraints.
            </p>
          </div>
        </div>
      </section>
      <section className="section container" aria-labelledby="engagement-title">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Working together</p>
            <h2 id="engagement-title">Start where you need us.</h2>
          </div>
          <p>
            Every engagement starts with a focused qualification conversation.
            From there, we define the right engineering path before
            implementation begins.
            <br />
            <a className="text-link" href="/how-we-work">
              How we work <span aria-hidden="true">&#8599;</span>
            </a>
          </p>
        </div>
        <div className="engagement-grid">
          {[
            [
              "Architecture & technical assessment",
              "Understand the constraints, review the existing system and define a practical technical direction.",
            ],
            [
              "Product & system development",
              "Design and implement a new product, platform or major subsystem, from feasibility through delivery.",
            ],
            [
              "Modernization & integration",
              "Evolve legacy software, connect existing systems and strengthen production operation.",
            ],
            [
              "Specialist engineering",
              "Bring senior expertise to embedded/IoT, AI, difficult debugging, performance or reliability problems.",
            ],
          ].map(([title, description], index) => (
            <article key={title}>
              <span className="service-number">0{index + 1}</span>
              <h3>{title}</h3>
              <p>{description}</p>
            </article>
          ))}
        </div>
      </section>
      <section
        id="contact"
        className="contact-section"
        aria-labelledby="contact-title"
      >
        <div className="container contact-grid">
          <div>
            <p className="eyebrow">Start a technical conversation</p>
            <h2 id="contact-title">
              Have a difficult
              <br />
              software problem?
            </h2>
            <p>
              Whether you’re building a product, connecting hardware and
              software, putting AI into production or modernizing a legacy
              platform, let’s talk about the system you need.
            </p>
          </div>
          <div className="contact-details">
            <p>
              Tell us what you’re building, what’s getting in the way and where
              you need engineering support.
            </p>
            <a className="button" href="/contact">
              Discuss your project <Arrow />
            </a>
            <a
              className="email-link"
              href="mailto:contact@quantingo.com"
              onClick={() => track("contact_email_click")}
            >
              contact@quantingo.com
            </a>
            <span className="contact-note">
              Email us directly to start the conversation.
            </span>
          </div>
        </div>
      </section>
    </Layout>
  );
}
